// BM-H398 - client-facing copy slots owned by the supervising director (VC).
//
// Both strings below are regulatory content supplied verbatim on
// 8 September 2026 (see /app/memory/bm-h398-copy-slots.md in the fork, and the
// decisions file in this repo). Rules that bind every render site:
//
//   * block-level content in the question flow, never inside a tooltip
//   * paragraph breaks preserved as separate <p> elements
//   * no line-clamp, no <details>, no accordion, no "read more" on mobile
//   * MIRROR_SPLIT_MESSAGE names no section; add a new slot rather than edit it
//   * any change to SEVERANCE_JOINT_DECISION_GUIDANCE needs the copy owner's sign-off
//
// A slot is born as a SentinelString. Constructing one raises in dev and test
// so a placeholder cannot survive a local run; in production it renders as
// nothing rather than throwing at the client. The preflight release gates
// (scripts/preflight_release_gates.py, gate sentinel_strings, a required
// check on main) scan every source file under frontend/src and block the
// merge while the marker is present. Belt and braces; the braces do the work.

const IS_PRODUCTION = typeof process !== "undefined" && process.env && process.env.NODE_ENV === "production";

export class SentinelString {
  constructor(marker) {
    this.marker = marker;
    if (!IS_PRODUCTION) {
      throw new Error(`Client-facing copy slot is still a sentinel (${marker}). The copy owner supplies this text.`);
    }
  }

  toString() {
    return "";
  }

  get paragraphs() {
    return [];
  }
}

export class BlockCopy {
  constructor(heading, paragraphs) {
    this.heading = heading;
    this.paragraphs = paragraphs;
    Object.freeze(this.paragraphs);
    Object.freeze(this);
  }

  toString() {
    return [this.heading, ...this.paragraphs].join("\n\n");
  }
}

export function isSentinel(value) {
  return value instanceof SentinelString;
}

// Shown immediately above `s2a_joint_assets.severance_decision`, before the
// client answers. Always shown when the question is shown.
export const SEVERANCE_JOINT_DECISION_GUIDANCE = new BlockCopy(
  "This one is a decision for both of you",
  [
    "In law either joint owner can sever alone, by giving the other written notice. But because we are acting for you both, this is a decision we will only take forward if you both agree.",
    "At the moment, if one of you dies, your share of the property passes automatically to the other. That happens whatever your will says. Severing your joint tenancy changes that. Each of you would then own a defined share that passes under your own will instead. It is what makes a Family Protection Will actually work.",
    "Because it changes how you both own your home, we can only do it if you both agree. We cannot sever for one of you and not the other, and we cannot act on this from one of you on your own.",
    "If you are unsure, or if the two of you do not agree, say so here rather than guessing. That is not a problem and it will not hold anything up. One of our solicitors will talk it through with you both.",
    "The one thing we need to avoid is the two of you giving us instructions that pull in opposite directions. If that happens, we may not be able to advise either of you properly while we are acting for you both, and we could reach a point where we can only continue for one of you. Telling us early is usually what stops it getting there.",
  ],
);

// Shown after a non-administrative divergence has taken the instruction off
// the mirror path. Generic by design; the divergence display names the point
// of difference separately.
export const MIRROR_SPLIT_MESSAGE = new BlockCopy(
  "You have each answered this differently",
  [
    "That is allowed. Wills do not have to match, and plenty of couples make different choices for perfectly good reasons.",
    "It does mean these are no longer mirror wills. We will prepare a separate will for each of you, reflecting what each of you has told us. The fee for that is different from the couples fee, and we will confirm it to you in writing before any drafting starts.",
    "Before we go further, one of our solicitors will call you. We are not checking up on you. We need to be satisfied that each of you has made your own decision, freely, and that we can still act properly for both of you. Where two people want genuinely different things, there is a point at which a firm can only act for one of them, and it is our job to see that coming early rather than halfway through the work.",
    "Nothing you have entered is lost. Please carry on and finish the questionnaire, and we will pick this up on the call.",
  ],
);
